import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  HeartHandshake,
  Search,
  X,
  Heart,
  Inbox,
  Send,
  Clock,
} from "lucide-react";
import toast from "react-hot-toast";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useAuthAccess from "../hooks/useAuthAccess";
import {
  doc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import Navbar from "../Landing/Navbar";
import { db } from "../firebase";
import { canDisplayProfile } from "../utils/profileVisibility";
import ProfileCard from "../Components/ProfileCard";
import EmptyState from "../Components/EmptyState";
import ConfirmDialog from "../Components/ConfirmDialog";
import { ProfileCardGridSkeleton } from "../Components/Skeleton";
import {
  fetchInterestCounts,
  fetchInterestPage,
  fetchProfilesByIds,
  fetchUserDoc,
} from "../services/firestoreQueries";


const statusConfig = {
  pending: { tone: "bg-amber-50 text-amber-700 border border-amber-100", Icon: Clock, label: "Pending" },
  accepted: { tone: "bg-emerald-50 text-emerald-700 border border-emerald-100", Icon: Check, label: "Accepted" },
  declined: { tone: "bg-rose-50 text-rose-700 border border-rose-100", Icon: X, label: "Declined" },
};

const InterestSkeleton = () => (
  <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
    {Array.from({ length: 4 }).map((_, i) => (
      <ProfileCardGridSkeleton key={i} />
    ))}
  </div>
);

const Interests = () => {
  const { user, profile: myProfile, loading: authLoading } = useAuthAccess();
  const [tab, setTab] = useState("received");
  const [search, setSearch] = useState("");
  const [pendingDecline, setPendingDecline] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const sentinelRef = useRef(null);
  const queryClient = useQueryClient();

  const blockedQuery = useQuery({
    queryKey: ["userDoc", user?.uid],
    queryFn: async () => {
      if (!user) return { blocked: [], shortlisted: [] };
      return await fetchUserDoc(user.uid);
    },
    enabled: !!user,
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  const interestCountsQuery = useQuery({
    queryKey: ["interestCounts", user?.uid],
    queryFn: async () => fetchInterestCounts(user.uid),
    enabled: !!user,
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  const interestsQuery = useInfiniteQuery({
    queryKey: ["interests", tab, user?.uid],
    queryFn: fetchInterestPage,
    enabled: !!user,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 300000,
    cacheTime: 1800000,
    retry: 1,
  });

  const interests = useMemo(
    () => interestsQuery.data?.pages.flatMap((page) => page.interests) ?? [],
    [interestsQuery.data]
  );
  const blockedIds = useMemo(
    () => blockedQuery.data?.blocked || [],
    [blockedQuery.data]
  );
  const interestCounts = interestCountsQuery.data || { received: 0, sent: 0 };
  const profileIds = useMemo(
    () => [...new Set(interests.flatMap((it) => [it.fromUserId, it.toUserId]).filter(Boolean))],
    [interests]
  );

  const profilesQuery = useQuery({
    queryKey: ["interestProfiles", JSON.stringify(profileIds)],
    queryFn: async () => fetchProfilesByIds(profileIds),
    enabled: profileIds.length > 0,
    staleTime: 300000,
    gcTime: 1800000,
    retry: 1,
    onSuccess: (data) => {
      // populate individual profile cache entries to avoid duplicate reads across pages
      try {
        Object.keys(data || {}).forEach((id) => {
          queryClient.setQueryData(["profile", id], data[id]);
        });
      } catch (e) {
        // ignore caching errors
      }
    },
  });
  const profiles = useMemo(
    () => profilesQuery.data || {},
    [profilesQuery.data]
  );
  // True only when we have interests to show but profiles haven't loaded yet
  const profilesLoading = profileIds.length > 0 && profilesQuery.isLoading;

  const updateStatusMutation = useMutation({
    mutationFn: async ({ interestId, status }) => {
      await updateDoc(doc(db, "interests", interestId), {
        status,
        updatedAt: serverTimestamp(),
      });
      return { interestId, status };
    },
    onSuccess: ({ interestId, status }) => {
      queryClient.setQueryData(["interests", tab, user?.uid], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            interests: page.interests.map((item) =>
              item.id === interestId ? { ...item, status } : item
            ),
          })),
        };
      });
    },
  });

  const loading = authLoading || interestsQuery.isLoading || blockedQuery.isLoading || interestCountsQuery.isLoading || profilesLoading;
  const loadingMore = interestsQuery.isFetchingNextPage;
  const hasMore = interestsQuery.hasNextPage ?? false;

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading || loadingMore || !user) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      interestsQuery.fetchNextPage();
    }, { rootMargin: "300px 0px" });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, user, interestsQuery]);

  const tabInterests = useMemo(() => {
    if (!user) return [];
    return interests
      .filter((it) => (tab === "received" ? it.toUserId === user.uid : it.fromUserId === user.uid))
      .sort((a, b) => String(b.createdAt?.seconds || "").localeCompare(String(a.createdAt?.seconds || "")));
  }, [interests, tab, user]);

  const visibleInterests = useMemo(() => {
    // Don't render anything while profiles are still loading — avoids
    // the "Profile unavailable" flash before data arrives.
    if (profilesLoading) return [];

    const term = search.trim().toLowerCase();
    return tabInterests
      .map((interest) => {
        const otherId = tab === "received" ? interest.fromUserId : interest.toUserId;
        const profile = profiles[otherId];
        return { interest, profile, otherId };
      })
      .filter(({ interest, profile, otherId }) => {
        // Hide interests where the other profile can't be found or is blocked/hidden
        if (!profile) return false;
        if (blockedIds.includes(otherId)) return false;
        if (!canDisplayProfile({ ...profile, id: otherId }, myProfile)) return false;

        // Status filter
        if (statusFilter !== "all" && interest.status !== statusFilter) return false;

        if (!term) return true;
        return [profile.fullName, profile.kmProfileId, profile.profession, profile.dateOfBirth, interest.status]
          .filter(Boolean).join(" ").toLowerCase().includes(term);
      });
  }, [blockedIds, myProfile, profiles, profilesLoading, search, tab, tabInterests, statusFilter]);

  const updateStatus = async (interest, status) => {
    setBusy(true);
    try {
      await updateStatusMutation.mutateAsync({ interestId: interest.id, status });
      toast.success(status === "accepted" ? "Interest accepted." : "Interest declined.");
    } catch (err) {
      toast.error(err.message || "Could not update interest status.");
    } finally {
      setBusy(false);
      setPendingDecline(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="page-container pt-4 pb-app-nav sm:pt-6 lg:pt-8">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-5 flex items-end justify-between gap-3"
        >
          <div>
            <h1 className="font-display text-2xl font-bold text-ink-800 sm:text-3xl">Interests</h1>
            <p className="mt-1 text-[13.5px] text-ink-400">Manage interests you've sent and received</p>
          </div>
          <div className="hidden h-12 w-12 items-center justify-center rounded-2xl bg-brand-gradient text-white shadow-soft sm:inline-flex">
            <HeartHandshake size={22} />
          </div>
        </motion.div>

        {/* Tabs — with animated sliding indicator */}
        <div className="relative flex items-center rounded-2xl border border-sand-200 bg-sand-100 p-1">
          {/* Sliding pill */}
          <motion.div
            className="absolute top-1 bottom-1 rounded-xl bg-brand-gradient shadow-soft"
            animate={{ left: tab === "received" ? "4px" : "50%", width: "calc(50% - 4px)" }}
            transition={{ type: "spring", stiffness: 400, damping: 35 }}
          />
          {[
            { key: "received", label: "Received", Icon: Inbox },
            { key: "sent", label: "Sent", Icon: Send },
          ].map(({ key, label, Icon }) => {
            const active = tab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => { setTab(key); setSearch(""); setStatusFilter("all"); }}
                className={`relative z-10 flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-[13px] font-semibold transition-colors ${
                  active ? "text-white" : "text-ink-500"
                }`}
              >
                <Icon size={15} />
                {label}
                <span
                  className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[10.5px] font-bold transition-colors ${
                    active ? "bg-white/25 text-white" : "bg-white text-brand-700"
                  }`}
                >
                  {interestCounts[key] ?? 0}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search + View toggle */}
        <div className="mt-3 flex items-center gap-2">
          <div className="input-group flex-1 h-11">
            <Search size={16} className="text-ink-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, ID, profession..."
              className="text-[13px]"
            />
            {search && (
              <button onClick={() => setSearch("")} className="text-ink-400 hover:text-brand-600">
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Status filter */}
        <div className="mt-2 flex gap-1.5 overflow-x-auto pb-1">
          {[
            { key: "all", label: "All" },
            { key: "pending", label: "Pending" },
            { key: "accepted", label: "Accepted" },
            { key: "declined", label: "Declined" },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setStatusFilter(key)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold transition ${
                statusFilter === key
                  ? "bg-brand-gradient text-white shadow-soft"
                  : "border border-sand-200 bg-white text-ink-600 hover:border-brand-600 hover:text-brand-600"
              }`}
            >
              {label}
            </button>
          ))}
        </div>


        <div className="mt-5">
          {/* Swipeable content — drag left/right on mobile to switch tabs */}
          <motion.div
            key={tab}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.15}
            onDragEnd={(_, info) => {
              // swipe left → sent, swipe right → received
              if (info.offset.x < -60 && tab === "received") {
                setTab("sent"); setSearch(""); setStatusFilter("all");
              } else if (info.offset.x > 60 && tab === "sent") {
                setTab("received"); setSearch(""); setStatusFilter("all");
              }
            }}
            initial={{ opacity: 0, x: tab === "received" ? -24 : 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.22 }}
            style={{ touchAction: "pan-y" }}
          >
          {loading ? (
            <InterestSkeleton />
          ) : interestsQuery.isError ? (
            <EmptyState
              Icon={Heart}
              tone="rose"
              title="Unable to load interests"
              description={String(interestsQuery.error?.message || "Failed to load interests.")}
              action={
                <button onClick={() => interestsQuery.refetch()} className="btn-primary px-5">
                  Try again
                </button>
              }
            />
          ) : visibleInterests.length === 0 ? (
            <EmptyState
              Icon={Heart}
              tone="brand"
              title={search ? "No matches" : `No ${tab} interests yet`}
              description={search ? "Try a different search term." : "Browse profiles and send interests to get started."}
              action={!search && tab === "received" ? <Link to="/matches" className="btn-primary px-5">Browse matches</Link> : null}
            />
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
                <AnimatePresence>
                  {visibleInterests.map(({ interest, profile }) => {
                    const status = statusConfig[interest.status] || statusConfig.pending;
                    const StatusIcon = status.Icon;
                    return (
                      <ProfileCard
                        key={interest.id}
                        profile={profile}
                        variant="grid"
                        badge={
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${status.tone}`}>
                            <StatusIcon size={10} /> {status.label}
                          </span>
                        }
                        actions={
                          <>
                            {tab === "received" && interest.status === "pending" && (
                              <>
                                <button
                                  onClick={() => updateStatus(interest, "accepted")}
                                  disabled={busy}
                                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-2xl bg-emerald-600 px-3.5 py-2.5 text-[12.5px] font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60 sm:flex-none"
                                >
                                  <Check size={14} /> Accept
                                </button>
                                <button
                                  onClick={() => setPendingDecline(interest)}
                                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-2xl border border-rose-200 bg-white px-3.5 py-2.5 text-[12.5px] font-semibold text-rose-600 transition hover:bg-rose-50 sm:flex-none"
                                >
                                  <X size={14} /> Decline
                                </button>
                              </>
                            )}
                          </>
                        }
                      />
                    );
                  })}
                </AnimatePresence>
              </div>

              {loadingMore && <InterestSkeleton />}
              {hasMore && !loadingMore && <div ref={sentinelRef} className="h-px" />}
            </>
          )}
          </motion.div>
        </div>
      </main>

      <ConfirmDialog
        open={!!pendingDecline}
        title="Decline this interest?"
        description="The user will not be notified, but they won't be able to send another request."
        Icon={X}
        tone="danger"
        confirmLabel="Yes, decline"
        loading={busy}
        onClose={() => !busy && setPendingDecline(null)}
        onConfirm={() => updateStatus(pendingDecline, "declined")}
      />
    </div>
  );
};

export default Interests;
